import React from 'react';
import styled from '@emotion/styled';
import dayjs from 'dayjs';
import { mqUser } from 'utils/responsive';

const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
    border-radius: 10px;
    overflow: hidden;
    background-color: var(--white);
    margin-top: 20px;
`;

const TableHeader = styled.th`
    padding: 12px;
    background-color: var(--main-font-color);
    border: 1px solid var(--gray);
    text-align: left;
    color: var(--white);
    font-size: var(--font-medium);
    font-weight: var(--font-bold);

    ${mqUser('SP')} {
        padding: 8px;
        font-size: var(--font-small);
    }
`;

const TableRow = styled.tr`
    &:nth-of-type(even) {
        background-color: var(--light-gray);
    }

    &:hover {
        background-color: #f0f0f0;
    }
`;

const TableCell = styled.td`
    padding: 12px;
    border: 1px solid #e0e0e0;
    font-size: var(--font-medium);

    ${mqUser('SP')} {
        padding: 8px;
        font-size: var(--font-small);
    }
`;

const EmptyMessage = styled.div`
    text-align: center;
    padding: 40px;
    color: #666;
    font-size: var(--font-medium);
`;

const StatusBadge = styled.span<{ status: string }>`
    display: inline-block;
    padding: 4px 12px;
    border-radius: 12px;
    font-size: var(--font-small);
    font-weight: var(--font-bold);
    ${(props) => {
        switch (props.status) {
            case 'waiting_apply':
                return `
                    background-color: #e3f2fd;
                    color: #1976d2;
                `;
            case 'pending':
                return `
                    background-color: #fff3cd;
                    color: #856404;
                `;
            case 'completed':
                return `
                    background-color: #d4edda;
                    color: #155724;
                `;
            case 'cancelled':
                return `
                    background-color: #f8d7da;
                    color: #721c24;
                `;
            default:
                return `
                    background-color: #e9ecef;
                    color: #495057;
                `;
        }
    }}
`;

const AmountCell = styled(TableCell)`
    text-align: right;
    font-weight: var(--font-bold);
    color: var(--main-color-blue);
`;

const ActionCell = styled(TableCell)`
    text-align: center;
`;

const ApplyButton = styled.button`
    background-color: var(--main-color-blue);
    color: var(--white);
    border: none;
    border-radius: 6px;
    padding: 8px 16px;
    font-size: var(--font-medium);
    font-weight: var(--font-bold);
    cursor: pointer;
    transition: background-color 0.2s;

    &:hover {
        background-color: #0056b3;
    }

    ${mqUser('SP')} {
        padding: 6px 12px;
        font-size: var(--font-small);
    }
`;

export interface TransferItem {
    id: number;
    invitee_name: string;
    invitee_type: string;
    status: string;
    status_display: string;
    transferred_at: string | null;
    amount: number;
    note: string;
    created_at: string;
    updated_at: string;
}

interface TransferListTableProps {
    transfers: TransferItem[];
    onApplyClick?: (transferId: number, amount: number) => void;
}

const getStatusLabel = (status: string): string => {
    const statusLabelMap: Record<string, string> = {
        waiting_apply: '申請待ち',
        pending: '振込待ち',
        completed: '振込完了',
        cancelled: 'キャンセル',
    };
    return statusLabelMap[status] || status;
};

export const TransferListTable: React.FC<TransferListTableProps> = ({ transfers, onApplyClick }) => {
    return (
        <Table>
            <thead>
                <tr>
                    <TableHeader>招待企業名</TableHeader>
                    <TableHeader>種別</TableHeader>
                    <TableHeader>ステータス</TableHeader>
                    <TableHeader>振込金額</TableHeader>
                    <TableHeader>振込日時</TableHeader>
                    <TableHeader>発生日時</TableHeader>
                    <TableHeader style={{ textAlign: 'center' }}>操作</TableHeader>
                </tr>
            </thead>
            <tbody>
                {transfers.length === 0 ? (
                    <tr>
                        <TableCell colSpan={7}>
                            <EmptyMessage>振込履歴はありません</EmptyMessage>
                        </TableCell>
                    </tr>
                ) : (
                    transfers.map((item) => (
                        <TableRow key={item.id}>
                            <TableCell>{item.invitee_name || '-'}</TableCell>
                            <TableCell>{item.invitee_type || '-'}</TableCell>
                            <TableCell>
                                <StatusBadge status={item.status}>{getStatusLabel(item.status)}</StatusBadge>
                            </TableCell>
                            <AmountCell>¥{Math.floor(item.amount).toLocaleString()}</AmountCell>
                            <TableCell>
                                {item.transferred_at ? dayjs(item.transferred_at).format('YYYY/MM/DD HH:mm') : '-'}
                            </TableCell>
                            <TableCell>{dayjs(item.created_at).format('YYYY/MM/DD HH:mm')}</TableCell>
                            <ActionCell>
                                {item.status === 'waiting_apply' && (
                                    <ApplyButton
                                        type="button"
                                        onClick={() => onApplyClick && onApplyClick(item.id, item.amount)}
                                    >
                                        振込申請
                                    </ApplyButton>
                                )}
                            </ActionCell>
                        </TableRow>
                    ))
                )}
            </tbody>
        </Table>
    );
};
